import { SIX_PLAYER_MVP_GAMES, SIX_PLAYER_MVP_SERVING } from './sixPlayerMvpSchedule.js';

// Helper function to shuffle an array in place
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const validateRotationDistribution = (
  playerNames,
  rotation,
  { expectedGames, sitsPerPlayer, playsPerPlayer },
  contextLabel = 'Rotation Validation'
) => {
  if (process.env.NODE_ENV === 'production') return;

  const issues = [];
  if (expectedGames && rotation.length !== expectedGames) {
    issues.push(`Expected ${expectedGames} games, received ${rotation.length}.`);
  }

  const sitOutCounts = {};
  const appearanceCounts = {};

  rotation.forEach((game) => {
    const sitsOutList =
      game.sitsOutList ||
      (game.sitsOut
        ? game.sitsOut
            .split(' & ')
            .map((name) => name.trim())
            .filter(Boolean)
        : []);

    sitsOutList.forEach((playerName) => {
      sitOutCounts[playerName] = (sitOutCounts[playerName] || 0) + 1;
    });

    [...(game.team1 || []), ...(game.team2 || [])].forEach((playerName) => {
      appearanceCounts[playerName] = (appearanceCounts[playerName] || 0) + 1;
    });
  });

  playerNames.forEach((name) => {
    if (
      typeof sitsPerPlayer === 'number' &&
      (sitOutCounts[name] || 0) !== sitsPerPlayer
    ) {
      issues.push(
        `Player ${name} should sit out ${sitsPerPlayer} times, found ${
          sitOutCounts[name] || 0
        }.`
      );
    }
    if (
      typeof playsPerPlayer === 'number' &&
      (appearanceCounts[name] || 0) !== playsPerPlayer
    ) {
      issues.push(
        `Player ${name} should appear in ${playsPerPlayer} games, found ${
          appearanceCounts[name] || 0
        }.`
      );
    }
  });

  if (issues.length > 0) {
    console.warn(`[${contextLabel}]`, issues.join(' '));
  }
};

// Function to generate a fair game rotation for 5 players based on the provided pattern
// Players must be in a shuffled order: players[0] = Player 1, players[4] = Player 5
export const generate5PlayerRotation = (players) => {
  if (players.length !== 5) {
    console.error('generate5PlayerRotation requires exactly 5 players.');
    return [];
  }

  // Randomize assignment so each session feels unique
  const randomizedPlayers = shuffleArray([...players]);

  const baseGames = [
    { teamA: [0, 1], teamB: [2, 3], sitsOut: 4 },
    { teamA: [2, 0], teamB: [4, 1], sitsOut: 3 },
    { teamA: [4, 2], teamB: [3, 0], sitsOut: 1 },
    { teamA: [1, 3], teamB: [0, 4], sitsOut: 2 },
    { teamA: [3, 4], teamB: [1, 2], sitsOut: 0 },
  ];

  const servingRounds = [
    { servingTeam: 'team1', servingPlayerIndex: 0 }, // Round 1: Team A, first player
    { servingTeam: 'team2', servingPlayerIndex: 0 }, // Round 2: Team B, first player
    { servingTeam: 'team1', servingPlayerIndex: 1 }, // Round 3: Team A, second player
    { servingTeam: 'team2', servingPlayerIndex: 1 }, // Round 4: Team B, second player
  ];

  const rotation = [];

  servingRounds.forEach((roundConfig) => {
    baseGames.forEach((gamePattern) => {
      const team1 = gamePattern.teamA.map((idx) => randomizedPlayers[idx].name);
      const team2 = gamePattern.teamB.map((idx) => randomizedPlayers[idx].name);
      const sitsOutList = [randomizedPlayers[gamePattern.sitsOut].name];

      const servingTeamPlayers =
        roundConfig.servingTeam === 'team1' ? team1 : team2;
      const server =
        servingTeamPlayers[roundConfig.servingPlayerIndex] || servingTeamPlayers[0];

      rotation.push({
        team1,
        team2,
        sitsOut: sitsOutList.join(' & '),
        sitsOutList,
        slot1A: team1[0],
        slot1B: team1[1],
        slot2A: team2[0],
        slot2B: team2[1],
        servingTeam: roundConfig.servingTeam,
        servingPlayerIndex: roundConfig.servingPlayerIndex,
        server,
      });
    });
  });

  validateRotationDistribution(
    randomizedPlayers.map((p) => p.name),
    rotation,
    { expectedGames: 20, sitsPerPlayer: 4, playsPerPlayer: 16 },
    '5-Player Rotation Validation'
  );

  return rotation;
};

// Function to generate a fair game rotation for 6 players based on the canonical 15-pair schedule
export const generate6PlayerRotation = (players) => {
  if (players.length !== 6) {
    console.error('generate6PlayerRotation requires exactly 6 players.');
    return [];
  }

  const randomizedPlayers = shuffleArray([...players]);

  const rotation = SIX_PLAYER_MVP_GAMES.map((gamePattern, idx) => {
    const roundConfig = SIX_PLAYER_MVP_SERVING[idx] || { servingTeam: 'team1', servingPlayerIndex: 0 };
    const team1 = gamePattern.teamA.map((i) => (typeof randomizedPlayers[i] === 'string' ? randomizedPlayers[i] : randomizedPlayers[i]?.name || ''));
    const team2 = gamePattern.teamB.map((i) => (typeof randomizedPlayers[i] === 'string' ? randomizedPlayers[i] : randomizedPlayers[i]?.name || ''));
    const sitsOutList = gamePattern.sitsOut.map((i) => (typeof randomizedPlayers[i] === 'string' ? randomizedPlayers[i] : randomizedPlayers[i]?.name || ''));

    const servingTeamPlayers = roundConfig.servingTeam === 'team1' ? team1 : team2;
    const server = servingTeamPlayers[roundConfig.servingPlayerIndex] || servingTeamPlayers[0];

    return {
      team1,
      team2,
      sitsOut: sitsOutList.join(' & '),
      sitsOutList,
      slot1A: team1[0],
      slot1B: team1[1],
      slot2A: team2[0],
      slot2B: team2[1],
      servingTeam: roundConfig.servingTeam,
      servingPlayerIndex: roundConfig.servingPlayerIndex,
      server,
    };
  });

  validateRotationDistribution(
    randomizedPlayers.map((p) => (typeof p === 'string' ? p : p?.name || '')),
    rotation,
    { expectedGames: 12, sitsPerPlayer: 4, playsPerPlayer: 8 },
    '6-Player Rotation Validation'
  );

  return rotation;
};

// MVP rotation for 3–6 players (2 players always play a classic singles match instead)
export const generateRotation = (players) => {
  const playerCount = players.length;

  if (playerCount < 3 || playerCount > 6) {
    console.error(`Rotation generation not supported for ${playerCount} players. Must be between 3-6.`);
    return [];
  }

  if (playerCount === 5) {
    return generate5PlayerRotation(players);
  }
  if (playerCount === 6) {
    return generate6PlayerRotation(players);
  }

  const rotation = [];

  if (playerCount === 3) {
    // 3 players: Based on spreadsheet pattern
    // Rotation 1 (1A serves): 3 games with rotating who sits out
    // Rotation 2 (1B serves): Same 3 games repeated
    // Total: 6 games (3 games × 2 serving positions)
    
    // Base game pattern (Rotation 1 - 1A serves)
    const baseGames = [
      { slot1A: 0, slot1B: 1, sitsOut: 2 },  // Game 1: 1A=0, 1B=1, OUT=2
      { slot1A: 2, slot1B: 0, sitsOut: 1 },  // Game 2: 1A=2, 1B=0, OUT=1
      { slot1A: 1, slot1B: 2, sitsOut: 0 },  // Game 3: 1A=1, 1B=2, OUT=0
    ];
    
    // Rotation 1: 1A serves (base games)
    for (const game of baseGames) {
      const playing1 = (game.sitsOut + 1) % 3;
      const playing2 = (game.sitsOut + 2) % 3;
      rotation.push({
        team1: [players[game.slot1A].name],
        team2: [players[game.slot1B].name],
        sitsOut: players[game.sitsOut].name,
        slot1A: players[game.slot1A].name,
        slot1B: players[game.slot1B].name,
        slot2A: players[game.slot1B].name, // 1B is the receiving player
        slot2B: '',
      });
    }
    
    // Rotation 2: 1B serves (same games, same player assignments, but 1B serves instead of 1A)
    for (const game of baseGames) {
      rotation.push({
        team1: [players[game.slot1A].name],
        team2: [players[game.slot1B].name],
        sitsOut: players[game.sitsOut].name,
        slot1A: players[game.slot1A].name, // Keep same position
        slot1B: players[game.slot1B].name, // Keep same position
        slot2A: players[game.slot1B].name, // 1B serves in rotation 2 (serves to opposite team)
        slot2B: '',
      });
    }
  } else if (playerCount === 4) {
    // 4 players: Balanced rotation ensuring all players team up equally
    // Randomize assignment so each session feels unique
    const randomizedPlayers = shuffleArray([...players]);
    
    // All 6 possible 2v2 matchups covering all pairings
    // Each pairing appears in exactly one of these base games
    const allBaseGames = [
      { teamA: [0, 1], teamB: [2, 3] }, // 0-1 vs 2-3
      { teamA: [0, 2], teamB: [1, 3] }, // 0-2 vs 1-3
      { teamA: [0, 3], teamB: [1, 2] }, // 0-3 vs 1-2
      { teamA: [1, 2], teamB: [0, 3] }, // 1-2 vs 0-3
      { teamA: [1, 3], teamB: [0, 2] }, // 1-3 vs 0-2
      { teamA: [2, 3], teamB: [0, 1] }, // 2-3 vs 0-1
    ];
    
    // For 4 serving rounds, we cycle through the 6 base games
    // Each round uses 4 games, selected to balance pairing distribution
    // This pattern ensures each pairing appears 5-6 times (difference of 1)
    const gameSelectionPerRound = [
      [0, 1, 3, 4], // Round 1: games covering 0-1,2-3,0-2,1-3,0-3,1-2,1-3,0-2
      [1, 2, 4, 5], // Round 2: games covering 0-2,1-3,1-2,0-3,1-3,0-2,2-3,0-1
      [2, 3, 5, 0], // Round 3: games covering 0-3,1-2,2-3,0-1,0-1,2-3,0-2,1-3
      [3, 4, 0, 1], // Round 4: games covering 1-2,0-3,1-3,0-2,0-1,2-3,0-2,1-3
    ];
    
    // Balanced serving: Rotate serving after each game so it's evenly distributed
    // Game 0: Player 0 serves, Game 1: Player 1 serves, Game 2: Player 2 serves, 
    // Game 3: Player 3 serves, Game 4: Player 0 serves (cycle repeats)
    // This ensures fair distribution regardless of how many games are played
    let gameCounter = 0;
    
    gameSelectionPerRound.forEach((gamesForThisRound, roundIndex) => {
      gamesForThisRound.forEach((gameIndex) => {
        const gamePattern = allBaseGames[gameIndex];
        const team1 = gamePattern.teamA.map((idx) => randomizedPlayers[idx].name);
        const team2 = gamePattern.teamB.map((idx) => randomizedPlayers[idx].name);
        
        // Rotate serving: each game gets the next player in rotation (0, 1, 2, 3, 0, 1, ...)
        const designatedServerPlayerIndex = gameCounter % 4;
        const designatedServer = randomizedPlayers[designatedServerPlayerIndex].name;
        
        // Determine which team the designated server is on
        let servingTeam = null;
        let servingPlayerIndexInTeam = -1;
        if (team1.includes(designatedServer)) {
          servingTeam = 'team1';
          servingPlayerIndexInTeam = team1.indexOf(designatedServer);
        } else if (team2.includes(designatedServer)) {
          servingTeam = 'team2';
          servingPlayerIndexInTeam = team2.indexOf(designatedServer);
        }
        
        rotation.push({
          team1,
          team2,
          sitsOut: '',
          slot1A: team1[0],
          slot1B: team1[1],
          slot2A: team2[0],
          slot2B: team2[1],
          servingTeam: servingTeam,
          servingPlayerIndex: servingPlayerIndexInTeam,
          server: designatedServer,
        });
        
        gameCounter++;
      });
    });
    
    validateRotationDistribution(
      randomizedPlayers.map((p) => p.name),
      rotation,
      { expectedGames: 16, sitsPerPlayer: 0, playsPerPlayer: 16 },
      '4-Player Rotation Validation'
    );
  }

  return rotation;
};

// Generate a single Classic match (2v2) for 4 players - preserves chosen pairings unless explicitly told to shuffle
export const generateClassicMatch = (players, shouldShuffle = false) => {
  if (!players || players.length !== 4) return null;
  const list = shouldShuffle ? shuffleArray([...players]) : [...players];
  const getName = (p) => (typeof p === 'object' && p !== null ? p.name : String(p || ''));
  const team1 = [getName(list[0]), getName(list[1])];
  const team2 = [getName(list[2]), getName(list[3])];
  const server = team1[0]; // Team 1 serves first
  return { team1, team2, server, id: 'doubles_1', isTieBreak: false, sets: [0, 0], games: [0, 0] };
};

// Helper function to parse 'DD/MM/YYYY' into a Date object for reliable chronological sorting.
export const parseDateString = (dateStr) => {
  if (
    !dateStr ||
    dateStr.toLowerCase() === 'current session' ||
    dateStr.toLowerCase() === 'no past sessions'
  ) {
    return new Date(0);
  }
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

